import React, { useEffect, useState } from 'react';
import { managerAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, Plus, Pencil, Trash2, Loader2, Building2, Search } from 'lucide-react';

const CATEGORIES = ['Maintenance', 'Repair', 'Utilities', 'Insurance', 'Taxes', 'Management', 'Supplies', 'Other'];

export default function ManagerExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterProperty, setFilterProperty] = useState('all');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ property_id: '', category: 'Maintenance', description: '', amount: '', date: new Date().toISOString().split('T')[0] });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [expRes, propRes] = await Promise.all([managerAPI.getExpenses(), managerAPI.getProperties()]);
      setExpenses(expRes.data);
      setProperties(propRes.data);
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.description || !form.amount || !form.date || !form.category) {
      toast.error('Please fill all required fields'); return;
    }
    try {
      if (editingExpense) {
        await managerAPI.editExpense(editingExpense.expense_id, { ...form, amount: parseFloat(form.amount) });
        toast.success('Expense updated');
      } else {
        await managerAPI.createExpense({ ...form, amount: parseFloat(form.amount) });
        toast.success('Expense added');
      }
      setDialogOpen(false);
      setEditingExpense(null);
      setForm({ property_id: '', category: 'Maintenance', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save expense'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await managerAPI.deleteExpense(id); toast.success('Expense deleted'); loadData(); }
    catch { toast.error('Failed to delete expense'); }
  };

  const openEdit = (exp) => {
    setEditingExpense(exp);
    setForm({ property_id: exp.property_id || '', category: exp.category, description: exp.description, amount: exp.amount, date: exp.date?.split('T')[0] || '' });
    setDialogOpen(true);
  };

  const filtered = expenses.filter(e => {
    if (filterProperty !== 'all' && e.property_id !== filterProperty) return false;
    if (search && !e.description?.toLowerCase().includes(search.toLowerCase()) && !e.category?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const total = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="expenses-title">Expenses</h1>
          <p className="text-muted-foreground">Total: {formatCurrency(total)}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditingExpense(null); setForm({ property_id: '', category: 'Maintenance', description: '', amount: '', date: new Date().toISOString().split('T')[0] }); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-2" /> Add Expense</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Property (optional)</Label>
                <Select value={form.property_id || 'none'} onValueChange={(v) => setForm({ ...form, property_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">General (No Property)</SelectItem>
                    {properties.map(p => <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Description *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Amount *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <Button onClick={handleSave} className="w-full" data-testid="save-expense-btn">{editingExpense ? 'Update' : 'Add'} Expense</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search expenses..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={filterProperty} onValueChange={setFilterProperty}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            {properties.map(p => <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No expenses found</CardContent></Card>
        ) : filtered.map(exp => (
          <Card key={exp.expense_id}>
            <CardContent className="flex items-center justify-between py-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{exp.category}</Badge>
                  {exp.property_id && <Badge variant="secondary"><Building2 className="w-3 h-3 mr-1" />{properties.find(p => p.property_id === exp.property_id)?.name || 'Unknown'}</Badge>}
                </div>
                <p className="font-medium mt-1">{exp.description}</p>
                <p className="text-sm text-muted-foreground">{formatDate(exp.date)}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-red-600">{formatCurrency(exp.amount)}</span>
                <Button size="icon" variant="ghost" onClick={() => openEdit(exp)} data-testid={`edit-expense-${exp.expense_id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(exp.expense_id)} data-testid={`delete-expense-${exp.expense_id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
